import React from 'react';
import './PermissionStatus.css';

export interface PermissionStatusProps {
  /** Whether microphone permission has been granted */
  hasPermission: boolean;
  /** Whether permission request is in progress */
  isRequestingPermission: boolean;
  /** Whether Web Audio API is supported */
  isSupported: boolean;
  /** Current error message, if any */
  error: string | null;
  /** Callback to request permission */
  onRequestPermission: () => void;
  /** Optional additional CSS class */
  className?: string;
}

/**
 * Permission status indicator component
 * Shows microphone access status and provides instructions when permission is denied
 * Displays compatibility warnings for unsupported browsers
 */
export const PermissionStatus: React.FC<PermissionStatusProps> = ({
  hasPermission,
  isRequestingPermission,
  isSupported,
  error,
  onRequestPermission,
  className = '',
}) => {
  const getStatusInfo = () => {
    if (!isSupported) {
      return {
        type: 'unsupported' as const,
        icon: '⚠️',
        title: 'Browser Not Supported',
        message: 'Your browser does not support the Web Audio API required for microphone access. Please use a modern browser like Chrome, Firefox, or Safari.',
        showButton: false,
      };
    }

    if (error) {
      return {
        type: 'error' as const,
        icon: '❌',
        title: 'Permission Error',
        message: error,
        showButton: !hasPermission,
      };
    }

    if (isRequestingPermission) {
      return {
        type: 'requesting' as const,
        icon: '⏳',
        title: 'Requesting Permission',
        message: 'Please allow microphone access in your browser to continue.',
        showButton: false,
      };
    }

    if (hasPermission) {
      return {
        type: 'granted' as const,
        icon: '✅',
        title: 'Microphone Access Granted',
        message: 'Ready to start recording audio for transcription.',
        showButton: false,
      };
    }

    return {
      type: 'denied' as const,
      icon: '🎤',
      title: 'Microphone Permission Required',
      message: 'Please grant microphone access to enable audio transcription.',
      showButton: true,
    };
  };

  const statusInfo = getStatusInfo();
  
  const containerClass = [
    'permission-status',
    `permission-status--${statusInfo.type}`,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClass} role="status" aria-live="polite">
      <div className="permission-status__content">
        <div className="permission-status__header">
          <span className="permission-status__icon" aria-hidden="true">
            {statusInfo.icon}
          </span>
          <h3 className="permission-status__title">
            {statusInfo.title}
          </h3>
        </div>
        
        <p className="permission-status__message">
          {statusInfo.message}
        </p>

        {statusInfo.showButton && (
          <div className="permission-status__actions">
            <button
              type="button"
              className="permission-status__button"
              onClick={onRequestPermission}
              disabled={isRequestingPermission}
            >
              {isRequestingPermission ? 'Requesting...' : 'Grant Microphone Access'}
            </button>
          </div>
        )}

        {statusInfo.type === 'denied' && !error && (
          <div className="permission-status__instructions">
            <h4>How to enable microphone access:</h4>
            <ul>
              <li>Click the microphone icon in your browser's address bar</li>
              <li>Select "Allow" when prompted</li>
              <li>If blocked, click the lock icon and change microphone settings</li>
              <li>Refresh the page if needed</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};